import { getAiService } from '../functions/aiService.js'

// AiModePicker — shell-mounted dropdown that lets the user pick the AI provider
// (Symbols Cloud / Org providers / Local agent) and the authorization mode (Ask
// permissions / Auto).
//
// Lives at the shell root (not inside CanvasPromptTextarea's ParamsBtn) so the
// picker's `position: fixed` lands in true viewport coordinates — anchoring
// inside the prompt textarea breaks because that parent has
// `transform: translateX(-50%)` which captures fixed-positioned descendants as
// its containing block.
//
// State contract:
//   _aiModePickerOpen   : boolean — visibility
//   _aiModePickerAnchor : { right, bottom } in px from the viewport's
//                         right/bottom edges, captured by ParamsBtn's onClick
//                         from el.node.getBoundingClientRect.
//   _aiModePickerTick   : integer — bumped by AiPickerRow.onClick so the
//                         children: factories re-evaluate and the active
//                         wash/check move immediately.
//   _aiSvcReady         : boolean — true once sdk.ai resolves at boot; picker
//                         children: factories gate on it.
//
// AiModePicker and AiModePickerBody share ONE set of section builders below
// (providerSection / authSection) so the two never drift — the option data,
// icons, headers and row wiring live in a single place.

// Uppercase caption header. Pins a single tight line (`lineHeight: '1'` + a
// small X2 bottom margin) — the span otherwise inherits the panel's loose
// typography (a ~59px box + 25px margin) which opened a big empty gap under
// each header. Returns a FRESH object per call so the two exports never share
// (and mutate) one node.
// The caption is the kit's mono micro-label (every section head in the
// redesign).
const sectionHeader = (key) => ({
  extends: 'AppMicroLabel',
  display: 'block',
  lineHeight: '1',
  margin: '0 0 X2 0',
  // The caption arrives as a polyglot KEY. `translate` returns its input
  // unchanged when a key does not resolve, so a plain caption still renders.
  text: (el) => el.call('polyglot', key)
})

// FA204 / frank trap — PROVIDER_OPTIONS/AUTH_OPTIONS + the two children
// factories used to be bare module-scope consts/functions; they silently
// read as undefined once a component is frank.toJSON'd for platform delivery
// (workspace d6ea1142, project_frank_drops_module_local_helpers). Each is now
// duplicated into BOTH AiModePicker's and AiModePickerBody's own `scope`
// block below (el.scope only resolves within the specific top-level export's
// tree, not across sibling exports in this file) and called via
// `el.scope.<name>`. `providerSection`/`authSection` still run at module-load
// time to build the static section objects — safe, since their OUTPUT here is
// only the inline wrapper below (which reads nothing but `el`).
const providerSection = () => ({
  flow: 'y',
  gap: 'V',
  SectionHeader: sectionHeader('ai.settings.provider'),
  Options: {
    flow: 'y',
    gap: 'V',
    children: (el, s) => el.scope.providerChildren(el, s),
    childExtends: 'AiPickerRow',
    childrenAs: 'state'
  }
})

const authSection = () => ({
  flow: 'y',
  gap: 'V',
  SectionHeader: sectionHeader('ai.settings.authorization'),
  Options: {
    flow: 'y',
    gap: 'V',
    children: (el, s) => el.scope.authChildren(el, s),
    childExtends: 'AiPickerRow',
    childrenAs: 'state'
  }
})

export const AiModePicker = {
  scope: {
    // Each option carries a `icon` (a real brand-icon-set glyph — verified
    // present: cloud / key / terminal / lock / play) rendered as
    // AiPickerRow's leading anchor.
    PROVIDER_OPTIONS: [
      {
        key: 'simone',
        label: 'ai.mode.providerCloud.label',
        sub: 'ai.mode.providerCloud.sub',
        icon: 'cloud'
      },
      {
        key: 'providers',
        label: 'ai.mode.providerOrg.label',
        sub: 'ai.mode.providerOrg.sub',
        icon: 'key'
      },
      {
        key: 'local',
        label: 'ai.mode.providerLocal.label',
        sub: 'ai.mode.providerLocal.sub',
        icon: 'terminal'
      }
    ],
    AUTH_OPTIONS: [
      {
        key: 'ask',
        label: 'ai.mode.permAsk.label',
        sub: 'ai.mode.permAsk.sub',
        icon: 'lock',
        authMode: true
      },
      {
        key: 'auto',
        label: 'ai.mode.permAuto.label',
        sub: 'ai.mode.permAuto.sub',
        icon: 'play',
        authMode: true
      }
    ],
    providerChildren(el, s) {
      void s.root?._aiModePickerTick
      void s.root?._aiSvcReady
      const ai = getAiService()
      if (!ai) return []
      const current = ai.getMode?.() || 'simone'
      return this.PROVIDER_OPTIONS.map((m) => ({ ...m, active: m.key === current }))
    },
    authChildren(el, s) {
      void s.root?._aiModePickerTick
      void s.root?._aiSvcReady
      const ai = getAiService()
      if (!ai) return []
      const current = ai.getAuthMode?.() || 'ask'
      return this.AUTH_OPTIONS.map((m) => ({ ...m, active: m.key === current }))
    }
  },
  hide: (el, s) => !s.root?._aiModePickerOpen,
  tag: 'div',
  position: 'fixed',
  bottom: (el, s) => {
    const a = s.root?._aiModePickerAnchor
    return a && typeof a.bottom === 'number' ? a.bottom + 'px' : '80px'
  },
  right: (el, s) => {
    const a = s.root?._aiModePickerAnchor
    return a && typeof a.right === 'number' ? a.right + 'px' : '24px'
  },
  // The popover recipe (DESIGN_SPEC §6.1): the `G` floor (the AiMenuPanel
  // width; it was a raw 320px), the 70vh cap with its own scroll, the card
  // radius, the in-app `dialog` surface on the overlay elevation.
  minWidth: 'G',
  maxWidth: '90vw',
  maxHeight: '70dvh',
  overflow: 'auto',
  padding: 'Z',
  round: 'radiusCard',
  theme: 'dialog',
  // Borderless — a surface theme may paint a 1px border via its longhand,
  // which DOMQL composes AFTER `border: 'none'` (the theme's longhand wins).
  // Explicit `borderWidth: 0` + the shorthand both so neither slot can apply.
  border: 'none',
  borderWidth: '0',
  shadow: 'overlay',
  zIndex: '200',
  flow: 'y',
  gap: 'V',
  textAlign: 'left',

  // Inner-click guard so the document mousedown listener below doesn't close
  // the picker on its own clicks.
  onClick: (e) => e.stopPropagation(),
  // Outside-press closes. FA503: declared, so DOMQL registers it once and
  // removes it in dispose() — no `el._docClickGuard` mirror field.
  onDocumentMousedown: {
    capture: true,
    handler: (ev, el) => {
      const node = el?.node
      if (!node) return
      if (node.contains(ev.target)) return
      // Also keep open if the click landed on the anchor button so ParamsBtn's
      // own onClick gets a chance to toggle (otherwise we close → open in one
      // click and the picker flickers shut).
      const anchor = document.querySelector('[data-key="ParamsBtn"]')
      if (anchor && anchor.contains(ev.target)) return
      const root = el.state?.root
      if (root?._aiModePickerOpen) root.update({ _aiModePickerOpen: false })
    }
  },

  ProviderSection: providerSection(),
  AuthSection: authSection()
}

// AiModePickerBody — body-only variant for the @symbo.ls/dropdown plugin. Same
// two sections as AiModePicker but no positioning, no outside-click guard.
// Renders inside the plugin's single DropdownRoot Panel via
// `el.call('openDropdown', { content: { extends: 'AiModePickerBody' } })`.
// Callers don't need to seed _aiModePickerOpen / _aiModePickerAnchor — the
// dropdown plugin owns those.
export const AiModePickerBody = {
  // Duplicated from AiModePicker's scope — see the frank-trap note above
  // `providerSection`. el.scope only resolves within THIS export's own tree.
  scope: {
    PROVIDER_OPTIONS: [
      {
        key: 'simone',
        label: 'ai.mode.providerCloud.label',
        sub: 'ai.mode.providerCloud.sub',
        icon: 'cloud'
      },
      {
        key: 'providers',
        label: 'ai.mode.providerOrg.label',
        sub: 'ai.mode.providerOrg.sub',
        icon: 'key'
      },
      {
        key: 'local',
        label: 'ai.mode.providerLocal.label',
        sub: 'ai.mode.providerLocal.sub',
        icon: 'terminal'
      }
    ],
    AUTH_OPTIONS: [
      {
        key: 'ask',
        label: 'ai.mode.permAsk.label',
        sub: 'ai.mode.permAsk.sub',
        icon: 'lock',
        authMode: true
      },
      {
        key: 'auto',
        label: 'ai.mode.permAuto.label',
        sub: 'ai.mode.permAuto.sub',
        icon: 'play',
        authMode: true
      }
    ],
    providerChildren(el, s) {
      void s.root?._aiModePickerTick
      void s.root?._aiSvcReady
      const ai = getAiService()
      if (!ai) return []
      const current = ai.getMode?.() || 'simone'
      return this.PROVIDER_OPTIONS.map((m) => ({ ...m, active: m.key === current }))
    },
    authChildren(el, s) {
      void s.root?._aiModePickerTick
      void s.root?._aiSvcReady
      const ai = getAiService()
      if (!ai) return []
      const current = ai.getAuthMode?.() || 'ask'
      return this.AUTH_OPTIONS.map((m) => ({ ...m, active: m.key === current }))
    }
  },
  tag: 'div',
  // The AiMenuPanel floor (was a raw 320px).
  minWidth: 'G',
  // Cap at 90vw so long provider labels can't push the dropdown panel wider
  // than the viewport on iPad / Split View (mirrors AiModePicker's 90vw cap).
  maxWidth: '90vw',
  flow: 'y',
  gap: 'V',
  textAlign: 'left',

  ProviderSection: providerSection(),
  AuthSection: authSection()
}
